import { Card, CardContent, Typography, useTheme, Box, Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import * as echarts from 'echarts';
import { getLastSevenDates } from "../utils/tool";
import { LinearProgress, useDataProvider } from "react-admin";

type EChartsOption = echarts.EChartsOption;
const formatDateToMMDD = (dateStr: string) => {
    const parts = dateStr.split('-');
    if (parts.length < 3) return dateStr;
    return `${parts[1]}-${parts[2]}`;
};

const lastSevenDates = getLastSevenDates();
export const NewMemberChart = () => {
    const dataProvider = useDataProvider();
    const chartLeftRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const [userStats, setUserStats] = useState<any[]>([]);

    // 获取用户统计数据
    useEffect(() => {
        const fetchUserStats = async () => {
            try {
                const { data } = await dataProvider.getUserStatDaily();
                setUserStats(data || []);
            } catch (error) {
                console.error("获取注册用户统计失败:", error);
            }
        };
        fetchUserStats();
    }, []);

    // 处理图表数据
    useEffect(() => {
        if (!chartLeftRef.current) return;

        const chartInstance = echarts.init(chartLeftRef.current);

        // 1. 创建日期映射，方便快速查找
        const dateMap: Record<string, number> = {};

        // console.log('userStats', userStats)
        userStats.forEach(stat => {
            const formattedDate = formatDateToMMDD(stat.dataDate);
            dateMap[formattedDate] = stat.total;
        });

        // 2. 计算每日新增用户数量
        const newUserData = lastSevenDates.map((date) => {
            return dateMap[date] || 0
        });

        // 3. 配置图表选项
        const option: EChartsOption = {
            grid: {
                top: 20,
                bottom: 0,
                left: 0,
                right: 20,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: lastSevenDates,
                axisLabel: {
                    color: theme.palette.text.secondary
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: theme.palette.text.secondary
                }
            },
            tooltip: {
                trigger: 'axis',
                formatter: (params: any) => {
                    const param = params[0];
                    return `${param.name}<br/>新增用户: ${param.value}`;
                }
            },
            series: [{
                type: 'line',
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(58, 77, 233, 0.8)' },
                        { offset: 1, color: 'rgba(58, 77, 233, 0.1)' }
                    ])
                },
                lineStyle: {
                    color: '#3A4DE9'
                },
                symbol: 'circle',
                symbolSize: 8,
                data: newUserData
            }]
        };

        chartInstance.setOption(option);

        // 响应式处理
        const resizeHandler = () => chartInstance.resize();
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.dispose();
        };
    }, [theme, userStats]); // 添加 userStats 依赖

    return (
        <div ref={chartLeftRef} style={{ width: '100%', height: '100%', minHeight: '200px' }} />
    );
};

export const MemberDataCard = (props: any) => {
    const theme = useTheme();
    const dataProvider = useDataProvider();
    const [chartData, setChartData] = useState([0, 0]);
    const [userTotal, setUserTotal] = useState(0);

    useEffect(() => {
        const fetchActivationData = async () => {
            try {
                let { data } = await dataProvider.getMallUserStat();
                setUserTotal(data.userTotal);
                setChartData([data.user7DayTotal, data.bindDeviceUser7DayTotal]);
            } catch (error) {
                console.error("获取注册用户统计失败:", error);
            }
        };
        fetchActivationData();
    }, []);

    return (
        <Card sx={{ flexGrow: 1, height: '100%' }}>
            <CardContent sx={{ height: '100%' }}>
                <Grid container spacing={2} >
                    <Grid item xs={6}>
                        <div style={{ color: '#1c3166', fontSize: '22px', fontWeight: 'bold' }}>移动端注册会员总数</div>
                        <Typography variant="h6">{userTotal}人</Typography>
                        <Box sx={{
                            height: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-around',
                            border: '1px solid #4e6d8c',
                            borderRadius: '5px',
                            padding: '10px',
                        }}>
                            <Box>
                                <Typography variant="body2" gutterBottom>
                                    注册会员总数(近7天): {chartData[0]}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={(chartData[0] != 0 ? (chartData[0] / userTotal) : 0) * 100}
                                    sx={{
                                        height: 10,
                                        borderRadius: 5,
                                        backgroundColor: theme.palette.grey[300],
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 5,
                                            backgroundColor: '#3A4DE9'
                                        }
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" gutterBottom>
                                    绑定设备的会员总数(近7天): {chartData[1]}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={(chartData[1] != 0 ? (chartData[1] / userTotal) : 0) * 100}
                                    sx={{
                                        height: 10,
                                        borderRadius: 5,
                                        backgroundColor: theme.palette.grey[300],
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 5,
                                            backgroundColor: '#4CAF50'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <div style={{ color: '#1c3166', fontSize: '22px', fontWeight: 'bold' }}>新增商城会员</div>
                        <Box sx={{ height: '100%' }}>
                            <NewMemberChart />
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};